import React from 'react';
import styled from 'styled-components/macro';
import { Typography } from '@deeptrue-frontend/ui';
import { ReactComponent as CrircleStrokeSvg } from 'assets/icons/circle-stroke.svg';
import { ProjectStatus } from 'consts/statuses';
import { useFormatMessage } from 'components/IntlProvider';

const Wrapper = styled.div`
  padding: 1.5rem;
  background-color: white;
  display: flex;
  justify-content: center;
  flex-direction: column;
  border: 1px solid #e3e5e8;
  margin-bottom: 0.875rem;
  border-radius: 0.375rem;
`;

const CricleStroke = styled(CrircleStrokeSvg)<{
  position: 'top' | 'left' | 'bottom' | 'right';
}>`
  position: absolute;

  ${({ position }) => {
    if (position === 'top') {
      return `
      top: -1px
    `;
    }

    if (position === 'right') {
      return `
      right: -4px;
      transform: rotate(90deg);
    `;
    }

    if (position === 'left') {
      return `
      left: -4px;
      transform: rotate(-90deg);
    `;
    }
    if (position === 'bottom') {
      return `
      bottom: -1px;
      transform: rotate(180deg);
    `;
    }
  }}
`;
const Circle = styled.div<{ status?: 'active' | 'semiActive' | 'none' }>`
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e8effc;
  margin-right: 1rem;
  border: 1px #477eeb;
  position: relative;

  ${({ status }) => {
    if (status === 'active') {
      return `
       background-color: #477eeb;
       p {
         color: white;
       }
      `;
    }
    if (status === 'semiActive') {
      return `
        border0: 2px dashed #477eeb !important;
      `;
    }
  }}
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  margin: 1rem 0;
`;

const Line = styled.div<{ status?: 'active' | 'semiActive' | 'none' }>`
  position: absolute;
  border-left: 2px solid #e8effc;
  width: 2px;
  height: 1.8rem;
  bottom: -2rem;
  ${({ status }) => {
    if (status === 'active') {
      return `
        border-color:#477eeb;

      `;
    }
    if (status === 'semiActive') {
      return `
       border-left: 2px dashed #477eeb !important;
      `;
    }
  }}
`;

interface StatusType {
  value: number;
  display: string;
}

interface CircleProps {
  value: number;
  status: 'active' | 'semiActive' | 'none';
  noLine?: boolean;
  emptyLine?: boolean;
}
const CircleBox = ({ value, status, noLine, emptyLine }: CircleProps) => {
  return (
    <Circle status={status}>
      {status === 'semiActive' && (
        <>
          <CricleStroke position="top" />
          <CricleStroke position="left" />
          <CricleStroke position="right" />
          <CricleStroke position="bottom" />
        </>
      )}
      <Typography weight="bold" variant="copy2">
        {value}
      </Typography>
      {!noLine && <Line status={emptyLine ? 'none' : status} />}
    </Circle>
  );
};

interface Props {
  status?: StatusType;
}

const ProjectProgressSubscribable = ({ status }: Props) => {
  const t = useFormatMessage();
  const isCollectingData = status?.value === ProjectStatus.COLLECTING_DATA;
  const isProjectDraft = status?.value === ProjectStatus.OFFER_DRAFT;
  const isCompleted = status?.value === ProjectStatus.FINISHED;

  return (
    <Wrapper>
      <Typography variant="copy1" mb="1.25rem" weight="medium">
        {t('project.progressBar.title', 'Project Progress')}
      </Typography>
      <Step>
        <CircleBox
          value={1}
          status={
            isCompleted ? 'active' : isProjectDraft ? 'semiActive' : 'active'
          }
          emptyLine={isCompleted ? false : isProjectDraft}
        />

        <Typography variant="copy1">
          {t('project.progressBar.briefingStep', 'Setup')}
        </Typography>
      </Step>

      <Step>
        <CircleBox
          value={2}
          status={
            isCompleted ? 'active' : isCollectingData ? 'semiActive' : 'none'
          }
        />

        <Typography variant="copy1">
          {t('project.progressBar.dataCollectionStep', 'Data collection')}
        </Typography>
      </Step>

      <Step>
        <CircleBox
          value={3}
          status={
            isCompleted ? 'active' : isCollectingData ? 'semiActive' : 'none'
          }
          noLine
        />
        <Typography variant="copy1">
          {t('project.progressBar.reportStep', 'Report')}
        </Typography>
      </Step>
    </Wrapper>
  );
};

export default ProjectProgressSubscribable;
